import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import { FlashList } from '@shopify/flash-list';
import { SPACING } from 'constants/design-system';
import { isEmpty } from 'lodash';

import colorsConfig from 'config/colors';
import { ItemSectionHeader } from 'modules/ChefScene/components';
import { EmptyList } from 'modules/ChefScene/components/EmptyList';
import { AppActions, AppSelectors } from 'store/App';
import { TAB_BARS_LIST } from 'store/App/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { OrderSelectors } from 'store/Orders';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';
import { useFormatDateTime } from 'util/helpers';
import { usePullToRefreshOrder } from 'util/hooks';
import { useNavigationContext } from 'util/hooks/useNavigationContext';

import { NavItem } from './Lists';

export const Drawer = () => {
  const { navigationState, activeRouteKey } = useNavigationContext();

  const logEvent = useLogEventCallback();

  const routeState = navigationState.routes[navigationState.index];
  const formatDateTime = useFormatDateTime();

  useUpdateEffect(() => {
    logEvent(
      AnalyticsEvents.tabChangedInOverviewScreen({
        tab: TAB_BARS_LIST[navigationState.index]?.value,
      }),
    );
  }, [navigationState.index]);

  const orders = useAppSelector((state) => {
    if (!routeState) return undefined;

    if (activeRouteKey === 'cooking') {
      return OrderSelectors.selectBeingPrepared(state);
    } else if (activeRouteKey === 'new') {
      return OrderSelectors.selectNotBeingPrepared(state);
    } else if (activeRouteKey === 'prepared') {
      return OrderSelectors.selectPrepared(state);
    } else if (activeRouteKey === 'scheduled') {
      const [today, rest] = OrderSelectors.selectScheduledGrouped(state);

      return [
        ...(!isEmpty(today)
          ? [{ data: today, date: new Date(), isToday: true }]
          : []),
        ...(rest
          ? rest.map((group) => ({
              data: group.items,
              date: group.date,
              isToday: false,
            }))
          : []),
      ];
    }
    return undefined;
  });

  const dispatch = useAppDispatch();

  useUpdateEffect(() => {
    dispatch((action, getState) => {
      const state = getState();
      const selectedOrderUUID = AppSelectors.selectOrderUUID(state);

      const flattenedOrders =
        activeRouteKey === 'scheduled'
          ? orders.flatMap((order) => order.data)
          : orders;

      const selectedOrderIndex = flattenedOrders.findIndex(
        (order) => order.uuid === selectedOrderUUID,
      );

      if (selectedOrderIndex === -1) {
        action(
          AppActions.setSelectedOrderUUID({
            orderUUID: flattenedOrders?.[0]?.uuid,
          }),
        );
      }
    });
  }, [orders]);

  const [isFetching, onRefresh] = usePullToRefreshOrder();
  const { t } = useTranslation();

  const headerOptions: { text: string; color: string } | undefined = (() => {
    switch (routeState?.key) {
      case 'cooking':
        return {
          text: t('overview.drawer.cookingOrders'),
          color: colorsConfig.ui.statuses.cooking,
        };
      case 'new':
        return {
          text: t('overview.drawer.newOrders'),
          color: colorsConfig.ui.statuses.active,
        };
      case 'prepared':
        return {
          text: t('overview.drawer.preparedOrders'),
          color: colorsConfig.ui.statuses.prepared,
        };
      case 'scheduled':
        return undefined;
    }
  })();

  const renderItem = useCallback(
    ({ item }) => <NavItem order={item} tab={activeRouteKey} />,
    [activeRouteKey],
  );

  const keyExtractor = useCallback((item) => item.uuid, []);

  const sageAreaInsets = useSafeAreaInsets();

  return (
    <View style={{ backgroundColor: colorsConfig.lightBlue2, height: '100%' }}>
      <View
        style={[
          {
            marginTop: sageAreaInsets.top,
          },
          styles.container,
        ]}
      >
        {/* {headerOptions && (
          <ItemSectionHeader
            label={headerOptions.text}
            color={headerOptions.color}
          />
        )} */}
        {!isEmpty(orders) ? (
          activeRouteKey === 'scheduled' ? (
            <SectionList
              sections={orders}
              renderItem={({ item }) => {
                return (
                  <NavItem
                    order={item}
                    tab={activeRouteKey}
                    isScheduled={true}
                  />
                );
              }}
              renderSectionHeader={({ section }) => {
                return (
                  <ItemSectionHeader
                    label={
                      section.isToday
                        ? t('common.today')
                        : formatDateTime(section.date, {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                          })
                    }
                    color={colorsConfig.ui.statuses.scheduled}
                  />
                );
              }}
              onRefresh={onRefresh}
              ItemSeparatorComponent={ItemSeparatorComponent}
              refreshing={isFetching}
              style={styles.sectionList}
            />
          ) : (
            <FlashList
              estimatedItemSize={100}
              data={orders}
              ItemSeparatorComponent={ItemSeparatorComponent}
              onRefresh={onRefresh}
              refreshing={isFetching}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
            />
          )
        ) : (
          <EmptyList onRefresh={onRefresh} isFetching={isFetching} />
        )}
      </View>
    </View>
  );
};

const ItemSeparatorComponent = () => (
  <View style={{ marginTop: SPACING.theme[6] }} />
);

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    flexShrink: 1,
    // marginHorizontal: 16,
    // marginTop: 12,
    // marginBottom: 12,
  },
  flastListContainer: {
    flex: 1,
  },
  sectionList: {
    flex: 1,
    paddingVertical: 4,
  },
});
